/**
 * HVILKET DØGNVINDU EN VISNING SPØR OM — og hvorfor det er én modul og ikke fire komponenter.
 *
 * =================================================================================================
 * VINDUET ER IKKE DET SAMME SOM DET MAN SER.
 *
 * Månedsrutenettet tegner 42 dager fordi uka begynner på en mandag og måneden ikke gjør det, og
 * agendaen leser fjorten dager framover fra den dagen man står på. Regner hver visning ut sitt eget
 * vindu, blir «hva spurte vi om» noe man må lese fire komponenter for å svare på — og to av dem
 * kommer til å være uenige om hvorvidt sluttiden er med.
 *
 * SLUTTEN ER EKSKLUSIV, ÉN GANG, HER. `to` er første øyeblikk ETTER vinduet, som i
 * `app.calendar_items`. Halvåpne intervaller er den ene formen der «dagen slutter 23:59:59.999»
 * aldri kan gå galt, og det er en feil som bare viser seg for det ene skiftet som slutter midnatt.
 *
 * =================================================================================================
 * LOKAL MIDNATT, IKKE UTC-MIDNATT.
 *
 * `new Date('2027-07-15')` er midnatt UTC, altså klokka to om natta i Oslo om sommeren. En uke
 * bygget av dem begynner og slutter to timer for sent, og det ene skiftet som starter 00:30 havner
 * i uka før. Konstruktøren med tall — `new Date(år, måned, dag)` — er lokal, og leserens lokale tid
 * er riktig her: det er DENNE maskinens kalender som tegnes.
 */
import { addDays, startOfMonth, windowFor, WEEKS_SHOWN, WEEK_LENGTH } from './month-grid';
import type { CalendarView } from './search-params';

/** Hvor mange dager agendaen leser framover. To uker er «det som kommer», ikke «resten av året». */
export const AGENDA_DAYS = 14;

export interface ViewWindow {
  /** ISO-øyeblikk, inklusiv. */
  readonly from: string;
  /** ISO-øyeblikk, EKSKLUSIV. */
  readonly to: string;
  /** Dagene visningen tegner, i rekkefølge. Tom for måneden, som har sin egen modell. */
  readonly days: readonly Date[];
}

/** Lokal midnatt den dagen. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Mandagen (eller søndagen) uka begynner på, etter leserens locale. */
export function startOfWeek(date: Date, firstWeekday: number): Date {
  const day = startOfDay(date);
  const shift = (day.getDay() - firstWeekday + 7) % 7;
  return addDays(day, -shift);
}

/**
 * Vinduet en visning spør API-et om, og dagene den tegner.
 *
 * `anchor` er dagen leseren står på — for måneden er det en dag i måneden, for uka en dag i uka.
 */
export function windowForView(view: CalendarView, anchor: Date, firstWeekday: number): ViewWindow {
  if (view === 'month') {
    /*
     * MÅNEDEN SPØR OM DE 42 DAGENE DEN TEGNER, ikke om de 30 måneden har. Cellene fra nabomånedene
     * er ekte celler med ekte innhold, og en måned som bare hentet sine egne dager ville tegnet dem
     * tomme — hvilket ser ut som «ingenting skjer 1. august» når det egentlig betyr «vi spurte ikke».
     */
    const days = windowFor(startOfMonth(anchor), firstWeekday);
    const first = days[0] ?? startOfDay(anchor);
    return {
      from: first.toISOString(),
      to: addDays(first, WEEKS_SHOWN * WEEK_LENGTH).toISOString(),
      days,
    };
  }

  if (view === 'week') {
    const first = startOfWeek(anchor, firstWeekday);
    return {
      from: first.toISOString(),
      to: addDays(first, WEEK_LENGTH).toISOString(),
      days: Array.from({ length: WEEK_LENGTH }, (_, index) => addDays(first, index)),
    };
  }

  if (view === 'day') {
    const first = startOfDay(anchor);
    return { from: first.toISOString(), to: addDays(first, 1).toISOString(), days: [first] };
  }

  const first = startOfDay(anchor);
  return {
    from: first.toISOString(),
    to: addDays(first, AGENDA_DAYS).toISOString(),
    days: Array.from({ length: AGENDA_DAYS }, (_, index) => addDays(first, index)),
  };
}

/**
 * Hvor langt ‹ og › flytter seg i hver visning — og det er IKKE alltid det vinduet er langt.
 *
 * Agendaen leser fjorten dager men blar SJU, fordi et bla som hopper over hele det man nettopp så
 * er et bla som mister sammenhengen. Måneden blar en måned og ikke 42 dager, av samme grunn som
 * den heter måned.
 */
export function stepped(view: CalendarView, anchor: Date, direction: 1 | -1): Date {
  if (view === 'month') {
    return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
  }
  if (view === 'week') return addDays(anchor, direction * WEEK_LENGTH);
  if (view === 'day') return addDays(anchor, direction);
  return addDays(anchor, direction * 7);
}

/** Dagen en visning er forankret i, lest av adressen. */
export function anchorOf(date: string | undefined, month: string | undefined, today: Date): Date {
  if (date !== undefined) {
    const parsed = new Date(`${date}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) return startOfDay(parsed);
  }
  if (month !== undefined) {
    const parsed = new Date(`${month}-01T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) return startOfDay(parsed);
  }
  return startOfDay(today);
}
